import { useAuth } from '@/lib/platform/AuthProvider';
import { useAppNavigation } from '@/lib/platform';
import { useEffect } from 'react';
import { useProfile } from '@/lib/api';
import { Loader } from '@/components/Loader';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isSignedIn, isLoaded, openSignIn } = useAuth();
  const { navigate, location } = useAppNavigation();

  const {data: profileData, isSuccess} = useProfile();

  // Show sign-in dialog when not signed in (no redirect -- URL stays as-is)
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      openSignIn({ preventClose: true });
    }
  }, [isLoaded, isSignedIn, openSignIn]);

  // Signed in but never finished signing up — send them back to it.
  //
  // Targets `/onboarding`, not `/quiz`: the flow creates the account part way
  // through, so this fires while the user is legitimately mid-signup. Pointing
  // it at the editor dropped them out of the flow with half their answers
  // stranded. The guard keeps it from firing when they're already there —
  // navigating to a bare path would also discard the query, which carries the
  // flow's step.
  useEffect(() => {
    if (!isSuccess || !isSignedIn) return;
    if (profileData?.data?.user?.quizCompletedAt) return;
    if (location.startsWith('/onboarding')) return;
    navigate('/onboarding');
  }, [
    isSuccess,
    isSignedIn,
    profileData?.data?.user?.quizCompletedAt,
    navigate,
    location,
  ]);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!isSignedIn) {
    return null;
  }

  return <>{children}</>;
}

